import React, { useState } from 'react';
import PropTypes from 'prop-types';

import DropdownSelect from '@strava/ui/DropdownSelect';
import colors from '@strava/ui/colors';
import I18n from 'utils/I18n';
import styles from './styles.scss';

const MIN_YEARS_BEFORE_TODAY = 125;

const I18N_MONTHS = (() => {
  const prefix = 'templates.settings.months.';
  return [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec'
  ].map((m) => prefix + m);
})();

const DropdownDatePicker = ({
  className,
  dateMax,
  dateSelected,
  dateChangeCallback,
  erroredControls,
  required,
  disabled,
  locale,
  showFullMonth,
  isSearchable
}) => {
  const [day, setDay] = useState(dateSelected ? dateSelected.getDate() : null);
  const [month, setMonth] = useState(
    dateSelected ? dateSelected.getMonth() + 1 : null
  ); // UI January => numeric value 1
  const [year, setYear] = useState(
    dateSelected ? dateSelected.getFullYear() : null
  );

  const dropdownConfig = {
    isDisabled: disabled,
    isFocused: false,
    isSearchable
  };

  const yearMax = dateMax.getFullYear();
  const monthMax = dateMax.getMonth();

  // DD
  const daySelectDefault = I18n.t(
    'templates.settings.birthday_select_defaults.day'
  );
  // MM
  const monthSelectDefault = I18n.t(
    'templates.settings.birthday_select_defaults.month'
  );
  // YYYY
  const yearSelectDefault = I18n.t(
    'templates.settings.birthday_select_defaults.year'
  );

  /**
   *  Helper function
   *  given, min =5, max =7 returns array [5,6,7]
   */
  const intArray = (min, max) =>
    Array.from({ length: max - min + 1 }, (_, i) => i + min);

  /**
   * Number in days in a given month for a specific year.
   */
  const numDaysInMonth = (y, m) => new Date(y, m, 0).getDate();

  const blankOption = { label: '', value: '' };

  /**
   * Day options that an athlete can choose from the day dropdown
   */
  const dayOptions = (() => {
    const options = required ? [] : [blankOption];
    intArray(
      1,
      year === yearMax && month === monthMax
        ? dateMax.getDate()
        : numDaysInMonth(year, month)
    ).forEach((d) => {
      options.push({ label: d.toString(), value: d });
    });
    return options;
  })();

  /**
   * Localized Month options that an athlete can choose from the month dropdown
   *
   * Note: January => 1 and December => 12, Unlike JS Date where January => 0
   */
  const monthOptions = (() => {
    const options = required ? [] : [blankOption];
    intArray(1, year === yearMax ? monthMax : 12).forEach((m) => {
      const label = showFullMonth ? I18n.t(I18N_MONTHS[m - 1]) : m.toString();
      options.push({ label, value: m });
    });
    return options;
  })();

  /**
   * Year options that an athlete can choose from the year dropdown
   */
  const yearOptions = (() => {
    const options = required ? [] : [blankOption];
    intArray(
      dateMax.getFullYear() - MIN_YEARS_BEFORE_TODAY,
      dateMax.getFullYear()
    )
      .reverse() // descending order - current year first
      .forEach((y) => {
        options.push({ label: y.toString(), value: y });
      });
    return options;
  })();

  const handleYearChange = (option) => {
    const newYear = option.value;
    setYear(newYear);
    let newDay = day;
    let newMonth = month;
    if (day > numDaysInMonth(newYear, month)) {
      newDay = null;
      setDay(newDay); // clear out the selected day
    }
    if (newYear === yearMax && month > monthMax) {
      newMonth = null;
      setMonth(newMonth); // clear out the month field (this avoids dates that are before the dateMax from being entered)
    }
    dateChangeCallback({ month: newMonth, day: newDay, year: newYear });
  };

  const handleMonthChange = (option) => {
    const newMonth = option.value;
    setMonth(newMonth);
    let newDay = day;
    if (day > numDaysInMonth(year, newMonth)) {
      newDay = null;
      setDay(newDay); // clear out the selected day
    }
    dateChangeCallback({ month: newMonth, day: newDay, year });
  };

  const handleDayChange = (option) => {
    const newDay = option.value;
    setDay(newDay);
    dateChangeCallback({ month, day: newDay, year });
  };

  /**
   * Changes selector border color to red when it has been flagged o have an error after
   * submission.
   */
  const showError = (control) => {
    return {
      control: (provided) => ({
        ...provided, // preserve all other initial styling
        borderColor: erroredControls.includes(control)
          ? colors.BRAND_DANGER
          : provided.borderColor
      })
    };
  };

  const monthSelector = (
    <DropdownSelect
      key="month"
      className={styles.month}
      options={monthOptions}
      value={monthOptions.find((o) => o.value === month)}
      onChange={handleMonthChange}
      placeholder={monthSelectDefault}
      styles={showError('month')}
      ariaLabel={monthSelectDefault}
      {...dropdownConfig}
    />
  );

  const daySelector = (
    <DropdownSelect
      key="day"
      className={styles.day}
      options={dayOptions}
      value={dayOptions.find((o) => o.value === day)}
      onChange={handleDayChange}
      placeholder={daySelectDefault}
      styles={showError('day')}
      ariaLabel={daySelectDefault}
      {...dropdownConfig}
    />
  );

  const yearSelector = (
    <DropdownSelect
      key="year"
      className={styles.year}
      options={yearOptions}
      value={yearOptions.find((o) => o.value === year)}
      onChange={handleYearChange}
      placeholder={yearSelectDefault}
      styles={showError('year')}
      ariaLabel={yearSelectDefault}
      {...dropdownConfig}
    />
  );

  /**
   * Determines date layout based on the language
   */
  const buildSelectors = () => {
    switch (locale) {
      case 'en-US':
        return [monthSelector, daySelector, yearSelector];
      case 'ko-KR':
      case 'ja-JP':
        return [yearSelector, monthSelector, daySelector];
      default:
        return [daySelector, monthSelector, yearSelector];
    }
  };

  const classNames = [styles.container];
  if (className) {
    classNames.push(className);
  }
  return (
    <div className={classNames.join(' ')} data-testid="date-picker-container">
      {buildSelectors()}
    </div>
  );
};

DropdownDatePicker.defaultProps = {
  className: null,
  dateMax: new Date(new Date().toDateString()), // today - we dont restrict options during account creation flow
  dateSelected: null,
  erroredControls: [],
  required: false,
  locale: window.navigator.userLanguage || window.navigator.language,
  disabled: false,
  showFullMonth: false,
  isSearchable: false
};

DropdownDatePicker.propTypes = {
  className: PropTypes.string,
  dateMax: PropTypes.instanceOf(Date),
  dateSelected: PropTypes.instanceOf(Date),
  dateChangeCallback: PropTypes.func.isRequired,
  erroredControls: PropTypes.arrayOf(PropTypes.oneOf(['day', 'month', 'year'])),
  locale: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  showFullMonth: PropTypes.bool,
  isSearchable: PropTypes.bool
};

export default DropdownDatePicker;
